// @ts-nocheck
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import FocusManager from 'models/propTypes/focusManager';

import appActions from 'common/support/applicationActions';
import device from 'common/support/device';
import FocusGroup from 'components/focus/FocusGroup';
import withRestoration from 'components/withRestoration';

import { getAppScalingFactor } from 'support/scalingHelper';

import FocusManagerHOC from 'components/hoc/FocusManagerHOC';

import textToSpeechManager from 'support/tts/TextToSpeechManager';
import styles from './ListRows.module.scss';

// We need to manually calculate the scaling factor (app width / 1920) since certain measurements
// are not scaled appropriately even though the element is affected by transform: scale
// getBoundingClientRect values are scaled automatically, whereas offset (ie. offsetTop/offsetLeft)
// and buffers need to be scaled manually
const scaling = getAppScalingFactor();

const ScrollDirection = {
  UP: 'up',
  DOWN: 'down',
};

class ListRows extends React.PureComponent {
  listRowsViewport = React.createRef();

  focusGroupRef = React.createRef();

  focusedIndex = 0;

  rowRefs = [];

  rowHeights = [];

  timeout = null;

  focusId = null;

  state = {
    isAnimating: false,
    top: 0,
    startIndex: 0,
    endIndex: 0,
  };

  constructor(props) {
    super(props);
    const { restorationState, focusManager, data } = props;
    this.focusId = props.focusId || focusManager.getNewUniqueId();
    this.state.endIndex = props.buffer;
    if (restorationState && data) {
      const {
        endIndex: oldEndIndex,
        startIndex: oldStartIndex,
      } = restorationState;
      let newEndIndex = oldEndIndex;
      let newStartIndex = oldStartIndex;
      if (oldEndIndex > data.length - 1) {
        const amountToShift = oldEndIndex - data.length - 1;
        newEndIndex = data.length - 1;
        newStartIndex = Math.max(0, oldStartIndex - amountToShift);
      }
      this.state = {
        ...restorationState,
        startIndex: newStartIndex,
        endIndex: newEndIndex,
      };
    }
  }

  componentDidMount() {
    const { data, focusManager } = this.props;
    focusManager.addFocusChangedListener(this.onFocusChanged);
    if (data && data.length) {
      this.rowHeights = this.rowRefs.map(
        item => item.current.getBoundingClientRect().height,
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { data, setRestorationState } = this.props;

    if (data && data.length) {
      for (let i = 0; i < this.rowRefs.length; i += 1) {
        if (this.rowRefs[i] && this.rowRefs[i].current) {
          this.rowHeights[i] = this.rowRefs[
            i
          ].current.getBoundingClientRect().height;
        }
      }
    }

    if (prevState !== this.state && this.state) {
      setRestorationState(this.state);
    }
  }

  componentWillUnmount() {
    const { focusManager } = this.props;
    focusManager.removeFocusChangedListener(this.onFocusChanged);
  }

  onFocusChanged = () => {
    const { focusManager } = this.props;
    if (!window || !focusManager.isFocusAncestor(this.focusId)) return;

    const {
      top: viewportTop,
      bottom: viewportBottom,
    } = this.listRowsViewport.current.getBoundingClientRect();

    const lastFocusedIndex = this.focusedIndex;
    const viewportHeight = viewportBottom - viewportTop;
    const { focusedIndex, state, rowRefs, rowHeights } = this;
    const refIndex = focusedIndex + state.startIndex;
    const rowRef = rowRefs[refIndex];

    const rowOffsetTop = rowRef.current
      ? rowRef.current.offsetTop * scaling
      : 0;

    const { shouldSnapRowToTop } = this.props;
    const snapRowToTop = shouldSnapRowToTop(refIndex);
    const newTop = snapRowToTop
      ? -rowOffsetTop
      : -rowOffsetTop + viewportHeight - rowHeights[refIndex];
    this.setState(
      {
        isAnimating: true,
        top: newTop,
      },
      () => {
        if (!this.timeout) {
          this.timeout = setTimeout(() => {
            this.timeout = null;
            snapRowToTop
              ? this.handleSnapping(lastFocusedIndex)
              : this.handleClipping(ScrollDirection.DOWN, lastFocusedIndex);
          }, 300);
        }
      },
    );
  };

  updateIndices = (newStartIndex, newEndIndex) => {
    const { data } = this.props;
    const { startIndex, endIndex, top } = this.state;

    const checkedStartIndex = Math.max(0, newStartIndex);
    const checkedEndIndex = Math.min(data.length - 1, newEndIndex);

    if (
      (checkedStartIndex !== startIndex || checkedEndIndex !== endIndex) &&
      this.rowHeights.length &&
      data.length >= checkedEndIndex
    ) {
      const shouldClip = checkedStartIndex !== startIndex;
      this.setState(
        {
          isAnimating: false,
          startIndex: checkedStartIndex,
          endIndex: checkedEndIndex,
        },
        () => {
          const clipIndex =
            checkedStartIndex > startIndex ? startIndex : checkedStartIndex;
          const clipOffset = shouldClip ? this.rowHeights[clipIndex] : 0;

          this.setState({
            top:
              checkedStartIndex > startIndex
                ? top + clipOffset
                : top - clipOffset,
          });
          if (this.focusGroupRef && this.focusGroupRef.current) {
            this.focusGroupRef.current.resetFocusIndex();
          }
        },
      );
    }
  };

  handleSnapping = lastFocusedIndex => {
    const { buffer } = this.props;
    const { startIndex } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = lastFocusedIndex + startIndex;

    const newStartIndex = focusedIndex - 1;
    const newEndIndex = focusedIndex + buffer;

    this.updateIndices(newStartIndex, newEndIndex);
  };

  handleClipping = (scrollDirection, lastFocusedIndex) => {
    const { buffer } = this.props;
    const { startIndex } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = lastFocusedIndex + startIndex;

    let newStartIndex;
    let newEndIndex;
    if (scrollDirection === ScrollDirection.UP) {
      newStartIndex = focusedIndex - 1;
      newEndIndex = focusedIndex + buffer;
    } else {
      newStartIndex = focusedIndex - buffer;
      newEndIndex = focusedIndex + 1;
    }

    this.updateIndices(newStartIndex, newEndIndex);
  };

  // Binary search for finding next focsable element
  getNextFocusableElement = (
    sourceElRect,
    focusableChildren,
    prevClosestEl = null,
  ) => {
    const sourceElMiddle = (sourceElRect.left + sourceElRect.right) / 2;

    const medianIndex = Math.floor(focusableChildren.length / 2);
    const targetEl = focusableChildren[medianIndex];

    const targetElMiddleValue =
      (targetEl.getBoundingClientRect().left +
        targetEl.getBoundingClientRect().right) /
      2;

    const targetElWidth = targetEl.getBoundingClientRect().width;

    const distanceDifference = sourceElMiddle - targetElMiddleValue;
    const absoluteDifference = Math.abs(distanceDifference);
    if (absoluteDifference <= targetElWidth) {
      // We are pretty close so lets just check where we are
      const nextIndexToCheck =
        distanceDifference >= 0 ? medianIndex + 1 : medianIndex - 1;

      // Ensure our index is always in bounds
      if (
        nextIndexToCheck >= 0 &&
        nextIndexToCheck < focusableChildren.length
      ) {
        const adjacentEl = focusableChildren[nextIndexToCheck];
        const adjacentElMiddle =
          (adjacentEl.getBoundingClientRect().left +
            adjacentEl.getBoundingClientRect().right) /
          2;

        return absoluteDifference < Math.abs(sourceElMiddle - adjacentElMiddle)
          ? targetEl
          : adjacentEl;
      }
    }

    let currClosestEl;
    if (!prevClosestEl) {
      currClosestEl = targetEl;
    } else {
      const prevElMiddle =
        (prevClosestEl.getBoundingClientRect().left +
          prevClosestEl.getBoundingClientRect().right) /
        2;

      const prevClosestElDistance = Math.abs(sourceElMiddle - prevElMiddle);

      currClosestEl =
        absoluteDifference < prevClosestElDistance ? targetEl : prevClosestEl;
    }

    const nextArray =
      targetElMiddleValue > sourceElMiddle
        ? focusableChildren.slice(0, medianIndex)
        : focusableChildren.slice(medianIndex);

    if (nextArray.length === 0 || focusableChildren.length === 1) {
      return currClosestEl;
    }

    return this.getNextFocusableElement(sourceElRect, nextArray, currClosestEl);
  };

  onActionOverride = action => {
    const { focusId, focusManager } = this.props;
    if (action === appActions.moveDown || action === appActions.moveUp) {
      // Let's first get our focusable children within this component (veritcally available)
      const currentFocusableElementRect = focusManager
        .getFocusableElement(focusManager.currentFocusId)
        .getBoundingClientRect();
      const currentRowIndex = this.focusGroupRef.current.focusIndex;
      const rowsAvailable = focusManager.getFocusableChildren(focusId);
      const lastIndex = rowsAvailable.length - 1;
      const nextIndex =
        action === appActions.moveDown
          ? currentRowIndex + 1
          : currentRowIndex - 1;
      let nextRow;

      if (nextIndex >= 0 && nextIndex <= lastIndex) {
        nextRow = rowsAvailable[nextIndex];

        const nextFocusableElement = this.getNextFocusableElement(
          currentFocusableElementRect,
          focusManager.getFocusableChildren(nextRow.getAttribute('data-fcsid')),
        );

        const {
          top: viewportTop,
          bottom: viewportBottom,
        } = this.listRowsViewport.current.getBoundingClientRect();

        if (nextRow) {
          const {
            top: newFocusTop,
            bottom: newFocusBottom,
          } = nextRow.getBoundingClientRect();

          if (
            this.timeout &&
            (newFocusTop < viewportTop || newFocusBottom > viewportBottom)
          ) {
            return true;
          }
        }

        focusManager.setCurrentFocus(
          nextFocusableElement.getAttribute('data-fcsid'),
        );
        return true;
      }
    }
    return false;
  };

  onEndOfNavigation = () => {
    const { data } = this.props;
    const { startIndex } = this.state;
    const index = this.focusedIndex + startIndex;
    if (index === 0 || index === data.length - 1) {
      const { ttsEndOfNavigation } = this.props;
      textToSpeechManager.add(ttsEndOfNavigation);
      textToSpeechManager.speak();
    }
  };

  onFocusIndexChanged = index => {
    const { startIndex } = this.state;
    const { onListRowsFocusIndexChanged } = this.props;
    this.focusedIndex = index;
    onListRowsFocusIndexChanged(index + startIndex);
  };

  resetIndex = async () => {
    return new Promise(res => {
      const { buffer } = this.props;
      this.focusedIndex = 0;
      this.setState(
        {
          isAnimating: true,
          top: 0,
          startIndex: 0,
          endIndex: buffer,
        },
        () => {
          const { focusManager } = this.props;
          const focusableRows = focusManager.getFocusableChildren(this.focusId);
          const firstRow = focusableRows?.length > 0 ? focusableRows[0] : null;
          const firstRowId = focusManager.getElementFocusId(firstRow);
          focusManager.setCurrentFocus(firstRowId);
          res();
        },
      );
    });
  };

  getFocusedIndex = () => this.focusedIndex;

  render() {
    const { className, renderItem, data, style } = this.props;
    const { top, isAnimating, startIndex, endIndex } = this.state;

    return (
      <div className={className} ref={this.listRowsViewport} style={style}>
        <FocusGroup
          className={classNames(
            {
              [styles.animate]: isAnimating && !device.isSlowDevice,
            },
            'text-nowrap',
            'd-block',
            'position-relative',
          )}
          ref={this.focusGroupRef}
          focusId={this.focusId && this.focusId.toString()}
          onActionOverride={this.onActionOverride}
          onFocusIndexChanged={this.onFocusIndexChanged}
          onFocusDelegated={this.onFocusIndexChanged}
          style={{
            transform: `translateY(${top / scaling}px)`,
            WebkitTransform: `translateY(${top / scaling}px)`,
          }}
          onEndOfNavigation={this.onEndOfNavigation}
        >
          {data &&
            !!data.length &&
            data.slice(startIndex, endIndex + 1).map((row, index) => {
              const totalIndex = startIndex + index;
              if (!this.rowRefs[totalIndex]) {
                this.rowRefs[totalIndex] = React.createRef();
              }
              return (
                <div
                  key={row.key || row.id || row.title}
                  ref={this.rowRefs[totalIndex]}
                >
                  {renderItem(row, index + startIndex)}
                </div>
              );
            })}
        </FocusGroup>
      </div>
    );
  }
}

ListRows.propTypes = {
  focusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  className: PropTypes.string,
  renderItem: PropTypes.func.isRequired,
  // Ignore forbid-prop-types because ListRows is supposed to accept a generic array
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.array.isRequired,
  onListRowsFocusIndexChanged: PropTypes.func,
  shouldSnapRowToTop: PropTypes.func,
  buffer: PropTypes.number.isRequired,
  restorationState: PropTypes.shape({
    isAnimating: PropTypes.bool.isRequired,
    top: PropTypes.number.isRequired,
    startIndex: PropTypes.number.isRequired,
    endIndex: PropTypes.number.isRequired,
  }),
  setRestorationState: PropTypes.func.isRequired,
  focusManager: FocusManager.isRequired,
  style: PropTypes.shape({
    transform: PropTypes.string,
  }),
  ttsEndOfNavigation: PropTypes.string,
};

ListRows.defaultProps = {
  className: null,
  onListRowsFocusIndexChanged: () => {},
  shouldSnapRowToTop: () => false,
  restorationState: null,
  style: {},
  ttsEndOfNavigation: null,
};

export default compose(
  FocusManagerHOC,
  withRestoration(props => props.restorationId),
)(ListRows);
